import React, { useState } from 'react';
import { Layout as AntLayout, Avatar, Dropdown, Space, Typography, Tag, Tooltip, message, Button } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CrownOutlined,
  ApiOutlined,
  WalletOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { USER_TYPE_LABELS, USER_TYPE_COLORS } from '../../config/supertokens';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { useIsMobile } from '../../hooks/useIsMobile';
import AppBottomNav from './AppBottomNav';
import AppNavDrawer from './AppNavDrawer';

const { Header, Content } = AntLayout;
const { Text } = Typography;

interface MinimalLayoutProps {
  children: React.ReactNode;
}

/**
 * MinimalLayout - Clean, minimal layout without sidebar navigation.
 * On mobile: compact header + bottom navigation bar (Instagram-style).
 * On desktop: sticky top header with user info + dropdown.
 */
const MinimalLayout: React.FC<MinimalLayoutProps> = ({ children }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { balance: creditBalance } = useCreditBalance();
  const isMobile = useIsMobile();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const brandIdFromPath = location.pathname.match(/^\/brands\/([^/]+)/)?.[1] ?? null;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navigateToBrandProfile = () => {
    if (brandIdFromPath) {
      navigate(`/brands/${brandIdFromPath}`);
      return;
    }
    navigate('/dashboard');
  };

  const navigateToBrandSettings = () => {
    if (brandIdFromPath) {
      navigate(`/brands/${brandIdFromPath}/settings`);
      return;
    }
    message.warning('Select a brand from the dashboard to open settings.');
    navigate('/dashboard');
  };

  const userMenuItems = [
    {
      key: 'user-info',
      label: (
        <div className="px-1 py-2">
          <div className="font-medium text-gray-900">{user?.name}</div>
          <div className="text-xs text-gray-500 mt-0.5">{user?.email}</div>
          <Tag
            color={USER_TYPE_COLORS[user?.user_type as keyof typeof USER_TYPE_COLORS] || 'default'}
            className="mt-2 text-xs"
          >
            {USER_TYPE_LABELS[user?.user_type as keyof typeof USER_TYPE_LABELS] || user?.user_type}
          </Tag>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'profile',
      icon: <UserOutlined className="text-gray-500" />,
      label: <span className="text-gray-700">Profile</span>,
      onClick: navigateToBrandProfile,
    },
    {
      key: 'settings',
      icon: <SettingOutlined className="text-gray-500" />,
      label: <span className="text-gray-700">Settings</span>,
      onClick: navigateToBrandSettings,
    },
    {
      key: 'mcp',
      icon: <ApiOutlined className="text-gray-500" />,
      label: <span className="text-gray-700">MCP Integration</span>,
      onClick: () => navigate('/settings/mcp'),
    },
    ...(isSuperAdmin ? [{
      key: 'admin',
      icon: <CrownOutlined className="text-amber-500" />,
      label: <span className="text-amber-600 font-medium">Admin Panel</span>,
      onClick: () => navigate('/admin'),
    }] : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined className="text-gray-500" />,
      label: <span className="text-gray-700">Logout</span>,
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout
      className="bg-gray-50 overflow-hidden flex flex-col"
      style={{ height: '100dvh' }}
    >
      {/* Header */}
      <Header
        className="bg-white border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 z-50"
        style={{
          height: '64px',
          lineHeight: '64px',
          padding: isMobile ? '0 16px' : '0 24px',
        }}
      >
        {/* Logo / mobile menu */}
        <div className="flex items-center gap-2 min-w-0">
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileDrawerOpen(true)}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
              aria-label="Open menu"
            />
          )}
          <div
            className="flex items-center gap-2.5 cursor-pointer min-w-0"
            onClick={() => navigate('/dashboard')}
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isSuperAdmin ? 'bg-amber-500' : 'bg-primary-600'
              }`}
            >
              <Text className="text-white font-semibold text-sm leading-none">
                {isSuperAdmin ? 'A' : 'B'}
              </Text>
            </div>
            {!isMobile && (
              <Text className="font-semibold text-gray-900 text-base">
                {isSuperAdmin ? 'Admin' : 'BRAI'}
              </Text>
            )}
            {isMobile && (
              <Text className="font-semibold text-gray-900 text-sm truncate">
                {isSuperAdmin ? 'Admin' : 'BRAI'}
              </Text>
            )}
          </div>
        </div>

        {isSuperAdmin && !isMobile && (
          <Tag className="ml-4 bg-amber-50 text-amber-700 border-amber-200">
            <CrownOutlined className="mr-1" />
            Super Admin
          </Tag>
        )}

        {/* Right side */}
        <div className="flex items-center">
          <Space size={isMobile ? 8 : 16}>
            {creditBalance !== null && (
              <Tooltip title="Your credit balance">
                <Tag
                  icon={<WalletOutlined />}
                  color={creditBalance > 0 ? 'blue' : 'red'}
                  className="cursor-pointer m-0 tap-scale"
                  style={{ fontSize: isMobile ? 11 : 14, padding: isMobile ? '2px 8px' : '4px 12px' }}
                  onClick={() => navigate('/credits')}
                >
                  {creditBalance.toLocaleString()}
                  {!isMobile && ' Credits'}
                </Tag>
              </Tooltip>
            )}

            {/* User name — hidden on mobile (shown in bottom nav "More" sheet) */}
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-gray-900">{user?.name}</div>
              <div className="text-xs text-gray-500">{user?.company}</div>
            </div>

            {/* Avatar dropdown — hidden on mobile (avatar is in AppBottomNav "More") */}
            {!isMobile && (
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Avatar
                  icon={<UserOutlined />}
                  className="cursor-pointer bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                  size={36}
                />
              </Dropdown>
            )}
          </Space>
        </div>
      </Header>

      {/* Content */}
      <Content
        className="bg-gray-50 flex-1 min-h-0 overflow-y-auto flex flex-col scroll-momentum"
        style={{
          padding: isMobile ? '16px 12px' : '24px',
          paddingBottom: isMobile
            ? 'calc(var(--bottom-nav-total, 80px) + 8px)'
            : '24px',
        }}
      >
        <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col">
          {children}
        </div>
      </Content>

      {/* Mobile bottom navigation */}
      <AppBottomNav />

      {/* Mobile nav drawer */}
      {isMobile && (
        <AppNavDrawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
        />
      )}
    </AntLayout>
  );
};

export default MinimalLayout;
